import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getEquipments, saveEquipment } from "../../components/Api/GmsEquipment";
import { SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  Eq_Id: 0,
  Eq_Code: "",
  Eq_Name_English: "",
  Eq_Name_Arabic: "",
  Status: true,
  PurchaseDate: "",
  ValueDate: "",
};

const emptyFilter = {
  Eq_Name_English: "",
  Eq_Name_Arabic: "",
  Status: null,
  PurchaseDateFrom: "",
  PurchaseDateTo: "",
  ValueDateFrom: "",
  ValueDateTo: "",
};

export default function EquipmentPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsEquipment');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsEquipment');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsEquipment');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsEquipment');
  const isAdmin = isUserAdmin();

  const [equipments, setEquipments] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    return { ...defaultForm, Status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadEquipments();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadEquipments();
    }
  }, [hasAppliedFilter]);

  const loadEquipments = async (filterData = filter) => {
    setLoading(true);
    try {
      const data = await getEquipments(filterData);
      setEquipments(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load equipments: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.Eq_Name_English) {
      Swal.fire("Error", "Please fill in Equipment Name (English)", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const result = await saveEquipment(form);
      
      if (result) {
        Swal.fire("Success", "Equipment saved successfully!", "success");
        handleClear();
        if (hasAppliedFilter) {
          loadEquipments();
        }
      }
    } catch (error) {
      let errorMessage = "Failed to save equipment";
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
          errorMessage = match ? match[1].trim() : error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (equipment) => {
    setEditLoadingRow(equipment.eq_Code);
    try {
      setForm({
        Eq_Id: equipment.eq_Id || 0,
        Eq_Code: equipment.eq_Code || "",
        Eq_Name_English: equipment.eq_Name_English || "",
        Eq_Name_Arabic: equipment.eq_Name_Arabic || "",
        Status: equipment.status,
        PurchaseDate: equipment.purchaseDate ? new Date(equipment.purchaseDate).toISOString().split('T')[0] : "",
        ValueDate: equipment.valueDate ? new Date(equipment.valueDate).toISOString().split('T')[0] : "",
      });
      setIsEditing(true);
      setActiveTab("1");
    } catch (error) {
      Swal.fire("Error", "Failed to load equipment data", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleClear = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    setForm({ ...defaultForm, Status: defaultStatus });
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadEquipments(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(false);
    setEquipments([]);
    setTotalCount(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getFilteredEquipments = () => {
    if (!hasAppliedFilter) return equipments;
    
    return equipments.filter(equipment => {
      const nameMatch = !filter.Eq_Name_English || 
        equipment.eq_Name_English.toLowerCase().includes(filter.Eq_Name_English.toLowerCase());
      const nameArMatch = !filter.Eq_Name_Arabic || 
        (equipment.eq_Name_Arabic && equipment.eq_Name_Arabic.toLowerCase().includes(filter.Eq_Name_Arabic.toLowerCase()));
      const statusMatch = filter.Status === null || equipment.status === filter.Status;
      return nameMatch && nameArMatch && statusMatch;
    });
  };

  const getPaginatedEquipments = () => {
    const filteredEquipments = getFilteredEquipments();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredEquipments.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      if (tab === "2") {
        loadEquipments();
      }
    }
  };

  const getEquipmentNameOptions = () => {
    return equipments.map(equipment => ({
      value: equipment.eq_Name_English,
      label: equipment.eq_Name_English
    }));
  };

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Equipment Management" breadcrumbItem="Incentives" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Equipment Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Equipment List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="Eq_Name_English">Equipment Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                type="text"
                                id="Eq_Name_English"
                                value={form.Eq_Name_English}
                                onChange={(e) => handleFormChange("Eq_Name_English", e.target.value)}
                                placeholder="Enter equipment name in English"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="Eq_Name_Arabic">Equipment Name (Arabic)</Label>
                              <Input
                                type="text"
                                id="Eq_Name_Arabic"
                                value={form.Eq_Name_Arabic}
                                onChange={(e) => handleFormChange("Eq_Name_Arabic", e.target.value)}
                                placeholder="Enter equipment name in Arabic"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="PurchaseDate">Purchase Date</Label>
                              <Input
                                type="date"
                                id="PurchaseDate"
                                value={form.PurchaseDate}
                                onChange={(e) => handleFormChange("PurchaseDate", e.target.value)}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="ValueDate">Value Date</Label>
                              <Input
                                type="date"
                                id="ValueDate"
                                value={form.ValueDate}
                                onChange={(e) => handleFormChange("ValueDate", e.target.value)}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        {hasDeletePermission && (
                          <Row>
                            <Col md={4}>
                              <FormGroup>
                                <div className="form-check form-switch mt-4">
                                  <Input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="Status"
                                    checked={form.Status}
                                    onChange={(e) => handleFormChange("Status", e.target.checked)}
                                  />
                                  <Label className="form-check-label" for="Status">
                                    {form.Status ? "Active" : "Inactive"}
                                  </Label>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        )}
                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Equipment" : "Save Equipment"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>
                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardHeader>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">Filter Equipment</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                        </CardHeader>
                        {showFilters && (
                        <CardBody>
                          <Row className="g-3">
                            <Col md={3}>
                              <Label for="filterEquipmentName">Equipment Name (English)</Label>
                              <Select
                                id="filterEquipmentName"
                                value={getEquipmentNameOptions().find(option => option.value === filter.Eq_Name_English)}
                                onChange={(selectedOption) => 
                                  handleFilterChange("Eq_Name_English", selectedOption ? selectedOption.value : "")
                                }
                                options={getEquipmentNameOptions()}
                                isClearable
                                isSearchable
                                placeholder="Search equipment name..."
                                className="react-select"
                                classNamePrefix="react-select"
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                    maxHeight: '200px'
                                  }),
                                  menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '200px'
                                  })
                                }}
                              />
                            </Col>

                            <Col md={2}>
                              <Label for="filterPurchaseDateFrom">Purchase Date From</Label>
                              <Input
                                type="date"
                                id="filterPurchaseDateFrom"
                                value={filter.PurchaseDateFrom}
                                onChange={(e) => handleFilterChange("PurchaseDateFrom", e.target.value)}
                              />
                            </Col>
                            <Col md={2}>
                              <Label for="filterPurchaseDateTo">Purchase Date To</Label>
                              <Input
                                type="date"
                                id="filterPurchaseDateTo"
                                value={filter.PurchaseDateTo}
                                onChange={(e) => handleFilterChange("PurchaseDateTo", e.target.value)}
                              />
                            </Col>
                            <Col md={2}>
                              <Label for="filterValueDateFrom">Value Date From</Label>
                              <Input
                                type="date"
                                id="filterValueDateFrom"
                                value={filter.ValueDateFrom}
                                onChange={(e) => handleFilterChange("ValueDateFrom", e.target.value)}
                              />
                            </Col>
                            <Col md={2}>
                              <Label for="filterValueDateTo">Value Date To</Label>
                              <Input
                                type="date"
                                id="filterValueDateTo"
                                value={filter.ValueDateTo}
                                onChange={(e) => handleFilterChange("ValueDateTo", e.target.value)}
                              />
                            </Col>
                            <Col md={3}>
                              <Label for="filterStatus">Status</Label>
                              <Select
                                id="filterStatus"
                                value={statusOptions.find(option => option.value === filter.Status)}
                                onChange={(selectedOption) =>
                                  handleFilterChange("Status", selectedOption ? selectedOption.value : null)
                                }
                                options={statusOptions}
                                isClearable
                                placeholder="Select Status"
                                className="react-select"
                                classNamePrefix="react-select"
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                    maxHeight: '200px'
                                  }),
                                  menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '200px'
                                  })
                                }}
                              />
                            </Col>
                            <Col md={9} className="d-flex align-items-end">
                              <div className="d-flex gap-2">
                                <Button
                                  color="primary"
                                  onClick={handleApplyFilter}
                                  className="waves-effect waves-light"
                                >
                                  Apply Filter
                                </Button>
                                <Button
                                  color="secondary"
                                  onClick={handleClearFilter}
                                  className="waves-effect waves-light"
                                >
                                  Clear Filter
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </CardBody>
                        )}
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Equipment List
                            </h5>
                            {hasEditOrHigher && (
                              <Button
                                color="primary"
                                onClick={() => toggle("1")}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Equipment
                              </Button>
                            )}
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading equipments...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="8%">Serial Number</th>
                                      <th width="20%">Equipment Name (En)</th>
                                      <th width="20%">Equipment Name (Ar)</th>
                                      <th width="12%">Purchase Date</th>
                                      <th width="12%">Value Date</th>
                                      <th width="10%">Status</th>
                                      <th width="8%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getPaginatedEquipments().length > 0 ? (
                                      getPaginatedEquipments().map((equipment, index) => (
                                        <tr key={equipment.eq_Id}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{equipment.eq_Name_English}</td>
                                          <td>{equipment.eq_Name_Arabic || "N/A"}</td>
                                          <td>{equipment.purchaseDate ? new Date(equipment.purchaseDate).toLocaleDateString('en-GB') : "N/A"}</td>
                                          <td>{equipment.valueDate ? new Date(equipment.valueDate).toLocaleDateString('en-GB') : "N/A"}</td>
                                          <td>
                                            <span className={`badge ${equipment.status ? 'bg-success' : 'bg-danger'}`}>
                                              {equipment.status ? "Active" : "Inactive"}
                                            </span>
                                          </td>
                                          <td>
                                            {hasEditOrHigher && (
                                              <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handleEdit(equipment)}
                                                disabled={editLoadingRow === equipment.eq_Code}
                                                className="waves-effect waves-light"
                                              >
                                                {editLoadingRow === equipment.eq_Code ? (
                                                  <ClipLoader size={12} color="#ffffff" />
                                                ) : (
                                                  <SquarePen size={12} />
                                                )}
                                              </Button>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="7" className="text-center">
                                          {hasAppliedFilter ? "No equipments found matching the filter criteria." : "No equipments available. Please add some equipments first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {getTotalPages() > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {getTotalPages()}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === getTotalPages()}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
